import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import { getIMDFStyle, getIMDFDisplayName, IMDF_LAYER_ORDER } from '../../utils/imdf-parser';

// 修復 Leaflet 預設圖標問題
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// 地圖模式
export type MapMode = 'view' | 'edit' | 'draw';

// 選取的特徵資訊
export interface SelectedFeature {
  feature: GeoJSON.Feature;
  layer: L.Layer;
  layerType: string;
}

export interface EditableLeafletMapProps {
  geoJsonData?: GeoJSON.FeatureCollection;
  height?: string;
  width?: string;
  className?: string;
  mode?: MapMode;
  selectedLayers?: string[]; // 要顯示的圖層
  onFeatureSelect?: (feature: SelectedFeature | null) => void;
  onFeatureEdit?: (feature: GeoJSON.Feature, layerType: string) => void;
  onFeatureCreate?: (feature: GeoJSON.Feature, layerType: string) => void;
  onFeatureDelete?: (feature: GeoJSON.Feature, layerType: string) => void;
  enableClustering?: boolean;
  fitBounds?: boolean;
  showPopup?: boolean; // 是否顯示 popup 和 tooltip（預設 true）
}

export interface EditableLeafletMapRef {
  map: L.Map | null;
  focusOnFeature: (feature: GeoJSON.Feature) => void;
  clearSelection: () => void;
  addImageOverlay: (imageUrl: string, bounds: L.LatLngBounds) => void;
  removeImageOverlay: (overlay: L.ImageOverlay) => void;
}

const EditableLeafletMap = forwardRef<EditableLeafletMapRef, EditableLeafletMapProps>(({
  geoJsonData,
  height = '400px',
  width = '100%',
  className = '',
  mode = 'view',
  selectedLayers = [],
  onFeatureSelect,
  onFeatureEdit,
  onFeatureCreate,
  onFeatureDelete,
  enableClustering = false,
  fitBounds = true,
  showPopup = true,
}, ref) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const geoJsonLayersRef = useRef<Map<string, L.GeoJSON>>(new Map());
  const markerClusterRef = useRef<L.MarkerClusterGroup | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<SelectedFeature | null>(null);
  const imageOverlaysRef = useRef<L.ImageOverlay[]>([]);

  // 初始化地圖
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const defaultLat = parseFloat(import.meta.env.VITE_MAP_DEFAULT_LAT || '25.0330');
    const defaultLng = parseFloat(import.meta.env.VITE_MAP_DEFAULT_LNG || '121.5654');
    const defaultZoom = parseInt(import.meta.env.VITE_MAP_DEFAULT_ZOOM || '13');

    // 創建地圖實例
    mapInstanceRef.current = L.map(mapRef.current, {
      preferCanvas: true, // 提升性能
    }).setView([defaultLat, defaultLng], defaultZoom);

    // 添加地圖底圖
    L.tileLayer(import.meta.env.VITE_TILE_SERVER || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapInstanceRef.current);

    // 初始化 Geoman 編輯工具
    if (mode === 'edit' || mode === 'draw') {
      mapInstanceRef.current.pm.addControls({
        position: 'topleft',
        drawCircle: false,
        drawCircleMarker: false,
        drawPolyline: true,
        drawRectangle: true,
        drawPolygon: true,
        drawMarker: true,
        editMode: true,
        dragMode: true,
        cutPolygon: false,
        removalMode: true,
      });

      // 綁定 Geoman 事件
      mapInstanceRef.current.on('pm:create', (e: any) => {
        const { layer, shape } = e;
        const feature: GeoJSON.Feature = {
          type: 'Feature',
          properties: {
            id: `new_${Date.now()}`,
            name: `New ${shape}`,
            created_at: new Date().toISOString(),
          },
          geometry: layer.toGeoJSON().geometry,
        };

        onFeatureCreate?.(feature, 'new');
      });

      mapInstanceRef.current.on('pm:edit', (e: any) => {
        const { layer } = e;
        const feature = (layer as any).feature;
        if (feature) {
          onFeatureEdit?.(feature, feature.properties?._file_type || 'unknown');
        }
      });

      mapInstanceRef.current.on('pm:remove', (e: any) => {
        const { layer } = e;
        const feature = (layer as any).feature;
        if (feature) {
          onFeatureDelete?.(feature, feature.properties?._file_type || 'unknown');
        }
      });
    }

    // 初始化聚合群組
    if (enableClustering) {
      markerClusterRef.current = (L as any).markerClusterGroup({
        chunkedLoading: true,
        spiderfyOnMaxZoom: false,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
      });
      if (markerClusterRef.current) {
        mapInstanceRef.current.addLayer(markerClusterRef.current);
      }
    }

    // 清理函數
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [mode, enableClustering]);

  // 更新 GeoJSON 圖層
  useEffect(() => {
    if (!mapInstanceRef.current || !geoJsonData) return;

    // 清除現有圖層
    geoJsonLayersRef.current.forEach((layer, layerType) => {
      if (markerClusterRef.current) {
        markerClusterRef.current.removeLayer(layer);
      } else {
        mapInstanceRef.current?.removeLayer(layer);
      }
    });
    geoJsonLayersRef.current.clear();

    // 按圖層類型分組特徵
    const layerFeatures = new Map<string, GeoJSON.Feature[]>();
    let selectedFeaturesCount = 0;
    let highlightedFeaturesCount = 0;
    
    geoJsonData.features.forEach(feature => {
      const layerType = feature.properties?._file_type || 'default';
      if (!layerFeatures.has(layerType)) {
        layerFeatures.set(layerType, []);
      }
      layerFeatures.get(layerType)!.push(feature);
      
      // 計算特殊標記的 features
      if (feature.properties?._selected) {
        selectedFeaturesCount++;
      }
      if (feature.properties?._highlighted_as_related) {
        highlightedFeaturesCount++;
      }
    });

    // 為每個圖層創建 GeoJSON 層
    // 處理所有存在的圖層類型，不僅僅是 IMDF_LAYER_ORDER 中的
    const allLayerTypes = [...IMDF_LAYER_ORDER];
    // 添加任何不在標準順序中的圖層類型
    layerFeatures.forEach((_, layerType) => {
      if (!allLayerTypes.includes(layerType)) {
        allLayerTypes.push(layerType);
      }
    });
    
    for (const layerType of allLayerTypes) {
      const features = layerFeatures.get(layerType);
      if (!features || features.length === 0) continue;

      // 檢查是否應該顯示此圖層
      if (selectedLayers.length > 0 && !selectedLayers.includes(layerType)) {
        continue;
      }

      const layerData: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features,
      };

      const geoJsonLayer = L.geoJSON(layerData, {
        style: (feature) => {
          return getIMDFStyle(feature!);
        },
        pointToLayer: (feature, latlng) => {
          const style = getIMDFStyle(feature);

          // 統一使用 Circle Marker，根據狀態調整大小
          let radius = 5; // 預設半徑
          if (feature.properties?._selected) {
            radius = 8; // 選中狀態：較大
          } else if (feature.properties?._highlighted_as_related) {
            radius = 6; // 高亮狀態：中等
          }

          const marker = L.circleMarker(latlng, {
            ...style,
            radius: radius,
          });

          // 如果啟用聚合，將點標記添加到聚合群組
          if (enableClustering && markerClusterRef.current) {
            markerClusterRef.current.addLayer(marker);
          }

          return marker;
        },
        onEachFeature: (feature, layer) => {
          // 綁定點擊事件
          layer.on('click', () => {
            const selectedInfo: SelectedFeature = {
              feature,
              layer,
              layerType: feature.properties?._file_type || 'unknown',
            };
            setSelectedFeature(selectedInfo);
            onFeatureSelect?.(selectedInfo);
          });

          // 只在 showPopup 為 true 時才添加 popup 和 tooltip
          if (showPopup) {
            // 添加彈出視窗
            const displayName = getIMDFDisplayName(feature);
            const popupContent = createPopupContent(feature, layerType);
            layer.bindPopup(popupContent);

            // 添加工具提示
            layer.bindTooltip(displayName, {
              permanent: false,
              direction: 'top',
              offset: [0, -10],
            });
          }

          // 儲存特徵引用到圖層
          (layer as any).feature = feature;

          // 啟用編輯模式
          if (mode === 'edit' && layer instanceof L.Polyline) {
            (layer as any).pm.enable({
              allowSelfIntersection: false,
            });
          }
        },
      });

      geoJsonLayersRef.current.set(layerType, geoJsonLayer);

      // 添加到地圖或聚合群組
      if (enableClustering && markerClusterRef.current) {
        // 點標記已在 pointToLayer 中添加到聚合群組
        // 這裡只添加非點幾何
        const nonPointLayer = L.geoJSON(layerData, {
          style: (feature) => getIMDFStyle(feature!),
          filter: (feature) => feature.geometry.type !== 'Point',
          onEachFeature: (feature, layer) => {
            // 重複上面的事件綁定邏輯
            layer.on('click', () => {
              const selectedInfo: SelectedFeature = {
                feature,
                layer,
                layerType: feature.properties?._file_type || 'unknown',
              };
              setSelectedFeature(selectedInfo);
              onFeatureSelect?.(selectedInfo);
            });

            // 只在 showPopup 為 true 時才添加 popup 和 tooltip
            if (showPopup) {
              const displayName = getIMDFDisplayName(feature);
              const popupContent = createPopupContent(feature, layerType);
              layer.bindPopup(popupContent);
              layer.bindTooltip(displayName, {
                permanent: false,
                direction: 'top',
                offset: [0, -10],
              });
            }

            (layer as any).feature = feature;

            if (mode === 'edit' && layer instanceof L.Polyline) {
              (layer as any).pm.enable({
                allowSelfIntersection: false,
              });
            }
          },
        });
        mapInstanceRef.current!.addLayer(nonPointLayer);
      } else {
        mapInstanceRef.current!.addLayer(geoJsonLayer);
      }
    }

    // 調整地圖視圖
    if (fitBounds && geoJsonData.features.length > 0) {
      // 使用更簡單直接的方法計算邊界，類似於 EnhancedLeafletMap
      const bounds = L.geoJSON(geoJsonData).getBounds();
      if (bounds.isValid()) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [20, 20] });
      }
    }
  }, [geoJsonData, selectedLayers, mode, enableClustering, fitBounds]);

  // 創建彈出視窗內容
  const createPopupContent = (feature: GeoJSON.Feature, layerType: string): string => {
    if (!feature.properties) return `<div>No properties</div>`;

    const filteredProperties = Object.entries(feature.properties)
      .filter(([key]) => !key.startsWith('_'))
      .slice(0, 10);

    return `
      <div style="max-width: 300px;">
        <h4 style="margin: 0 0 8px 0; color: #1f2937; font-weight: bold;">
          ${layerType} - ${getIMDFDisplayName(feature)}
        </h4>
        ${filteredProperties
          .map(([key, value]) => `
            <div style="margin-bottom: 4px;">
              <strong style="color: #374151;">${key}:</strong> 
              <span style="color: #6b7280;">${value || 'N/A'}</span>
            </div>
          `).join('')}
        ${feature.properties._source_file ? `
          <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
            <small style="color: #9ca3af;">來源: ${feature.properties._source_file}</small>
          </div>
        ` : ''}
      </div>
    `;
  };

  // 聚焦到特徵
  const focusOnFeature = useCallback((feature: GeoJSON.Feature) => {
    if (!mapInstanceRef.current || !feature.geometry) return;

    let bounds: L.LatLngBounds;

    if (feature.geometry.type === 'Point') {
      const coords = feature.geometry.coordinates as [number, number];
      const latlng = L.latLng(coords[1], coords[0]);
      mapInstanceRef.current.setView(latlng, 18);
      return;
    }

    // 對於其他幾何類型，使用邊界
    const tempLayer = L.geoJSON(feature);
    bounds = tempLayer.getBounds();
    
    if (bounds.isValid()) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }, []);

  // 清除選取
  const clearSelection = useCallback(() => {
    setSelectedFeature(null);
    onFeatureSelect?.(null);
  }, [onFeatureSelect]);

  // 添加圖片疊加
  const addImageOverlay = useCallback((imageUrl: string, bounds: L.LatLngBounds) => {
    if (!mapInstanceRef.current) return;

    const overlay = L.imageOverlay(imageUrl, bounds, {
      opacity: 0.7,
      interactive: true,
    });

    overlay.addTo(mapInstanceRef.current);
    imageOverlaysRef.current.push(overlay);
  }, []);

  // 移除圖片疊加
  const removeImageOverlay = useCallback((overlay: L.ImageOverlay) => {
    if (!mapInstanceRef.current) return;

    mapInstanceRef.current.removeLayer(overlay);
    const index = imageOverlaysRef.current.indexOf(overlay);
    if (index > -1) {
      imageOverlaysRef.current.splice(index, 1);
    }
  }, []);

  // 暴露方法給父組件
  useImperativeHandle(ref, () => ({
    map: mapInstanceRef.current,
    focusOnFeature,
    clearSelection,
    addImageOverlay,
    removeImageOverlay,
  }), [focusOnFeature, clearSelection, addImageOverlay, removeImageOverlay]);

  return (
    <div
      ref={mapRef}
      className={`editable-leaflet-map ${className}`}
      style={{ height, width }}
    />
  );
});

EditableLeafletMap.displayName = 'EditableLeafletMap';

export default EditableLeafletMap;